import { REDUX_ACTIONS } from 'utils/constants';
import { AppThunkP } from 'utils/frontendTypes';
import { auth, GoogleAuthProvider, FirebaseUserCredential } from 'utils/firebase';
import { toast } from 'react-toastify';
import { setUser } from 'utils/analytics';
import InAppRoutes, { pushProgressive } from 'utils/router';
import {
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  User,
  UserCredential,
} from 'firebase/auth';
import Cookies from 'js-cookie';
import { getMe, getOrganization, reloadMe, apiLogout } from './requests';
import { IS_LOCAL } from './utils';

const { REACT_APP_ENABLE_SSO } = process.env;

export const setLoggedOutCookie = (value: 'yes' | 'no') => {
  Cookies.set(`${process.env.REACT_APP_FIREBASE_COOKIE_BASE_NAME}.LoggedOut`, value, {
    path: '/',
    domain: process.env.REACT_APP_FIREBASE_COOKIE_DOMAIN,
    sameSite: 'lax',
    overwrite: true,
  });
};

export function loginAction(user: User, orgId?: number, options?: { withoutRedirect?: boolean }): AppThunkP {
  return async (dispatch) => {
    // Mark user as logged in
    setLoggedOutCookie('no');

    // TODO: Do something with the userCredential.additionalUserInfo
    const token = await user?.getIdToken();

    // Change organization by URL param, if necessary
    let redirectOrgId;
    const redirectUrlFragment = new URLSearchParams(window.location.search).get('redirect');
    if (redirectUrlFragment) {
      const [_newPath, redirectSearchParams] = redirectUrlFragment.split('?');
      if (redirectSearchParams) {
        redirectOrgId = parseInt(new URLSearchParams(redirectSearchParams).get('redirectOrgId'));
      }
    }
    const { user: orgUser, organization, accessOrgs } = await dispatch(getMe(redirectOrgId));

    setUser(orgUser.id, {
      organization,
      orgUser,
    });

    dispatch({ type: REDUX_ACTIONS.LOGIN, user, token, orgUser, organization, accessOrgs });
    if (orgId && organization.id !== orgId) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      await dispatch(handleSwitchOrg(orgId, options?.withoutRedirect));
    }
  };
}

export function loginWithoutAccountAction(user: User): AppThunkP {
  return async (dispatch) => {
    // Mark user as logged in
    setLoggedOutCookie('no');

    const token = await user?.getIdToken();
    const userCredential: UserCredential = {
      user,
      providerId: 'firebase',
      operationType: 'signIn',
    };

    dispatch({ type: REDUX_ACTIONS.LOGIN_WITHOUT_ACCOUNT, user, token, userCredential });
  };
}

function loginButDeleteFirebaseUserIfNotExists(user: FirebaseUserCredential): AppThunkP {
  return async (dispatch) => {
    // If we're running locally, we don't want to delete dev firebase users, that could mess with the dev site(s)
    if (IS_LOCAL) {
      if (REACT_APP_ENABLE_SSO === 'no') return dispatch(loginAction(user.user));
      return;
    }
    try {
      if (REACT_APP_ENABLE_SSO === 'no') await dispatch(loginAction(user.user));
    } catch (error) {
      // TODO: What do I do here? Do we only need to do this for Google or can we do it for all?
      if (error.message?.toLowerCase().includes('user not found')) {
        // Did a look up and the user was not found, we should delete the firebase login before continuing
        user?.user?.delete();
      }
      throw error;
    }
  };
}

export function logoutAction(): AppThunkP {
  return async (dispatch) => {
    setLoggedOutCookie('yes');
    await dispatch(apiLogout());
    dispatch({ type: REDUX_ACTIONS.LOGOUT });
    if (auth.currentUser) {
      setUser(null);
      await auth.signOut();
    }
  };
}

function firebaseGoogleLogin(): AppThunkP<FirebaseUserCredential> {
  return async () => {
    const provider = new GoogleAuthProvider();
    return signInWithPopup(auth, provider);
  };
}

export function login(email: string, password: string): AppThunkP {
  return async (dispatch) => {
    try {
      const existingUser = await signInWithEmailAndPassword(auth, email.toLowerCase(), password);
      if (REACT_APP_ENABLE_SSO === 'no') {
        console.debug('SSO Disabled, calling login action');
        await dispatch(loginAction(existingUser.user));
      }
    } catch (error) {
      if (error.message === 'User not found, you will be logged out.') {
        toast.dismiss();
        pushProgressive(InAppRoutes.CreateAccount);
        toast.success(
          'Looks like you already have a user account with another Gloo product. Setup a new account for your organization here using the same email address.',
        );
      } else {
        toast.error('There was an error logging in. Please ensure your email and password is correct.');
        console.error('LOGIN ERROR', error);
      }
    }
  };
}

export function loginWithGoogle(): AppThunkP {
  return async (dispatch) => {
    let user: FirebaseUserCredential;
    try {
      try {
        user = await dispatch(firebaseGoogleLogin());
      } catch (error) {
        // Do a toast message here if there's an error. Our API requests already do a toast for a failure
        toast.error(error.message);
        throw error;
      }
      await dispatch(loginButDeleteFirebaseUserIfNotExists(user));
    } catch (error) {
      console.error('error logging in with google', error);
    }
  };
}

export function forgotPassword(email: string): AppThunkP {
  return async () => {
    try {
      await sendPasswordResetEmail(auth, email.toLowerCase());
      toast.success('Password Reset Sent!');
    } catch (error) {
      toast.error(error.message);
    }
  };
}

export function handleSwitchOrg(orgId: number, bypassRedirectToHome?: boolean): AppThunkP {
  return async (dispatch, getState) => {
    await dispatch(reloadMe());
    const accessOrgs = getState().auth.accessOrgs || [];
    const accessOrgIds = accessOrgs.map((o) => o.id);

    if (!accessOrgIds.includes(orgId)) {
      return;
    }

    const newOrg = await dispatch(getOrganization(orgId));
    if (newOrg) {
      dispatch({ type: REDUX_ACTIONS.CHANGE_ORG, organization: newOrg });
    }

    await dispatch(reloadMe());

    if (!bypassRedirectToHome) {
      toast.success(`Switched to ${newOrg?.name || 'new account'}!`, { autoClose: 3000 });
      pushProgressive(InAppRoutes.Home);
    }
  };
}
